/**
 * Frankfurter API Service
 *
 * Free, open-source currency exchange rate API
 * Documentation: https://frankfurter.dev/
 *
 * Features:
 * - Daily updated rates (refreshed around 16:00 CET)
 * - Historical rates available
 * - No API key required
 * - Rate limit: No official limit, but be reasonable
 */

// The old api.frankfurter.app domain 301-redirects without CORS headers,
// which browsers block — only the .dev domain works from the client.
const FRANKFURTER_API_BASE = 'https://api.frankfurter.dev/v1';

export interface ExchangeRates {
  base: string;
  date: string;
  rates: Record<string, number>;
}

export interface HistoricalRate {
  amount: number;
  base: string;
  date: string;
  rates: Record<string, number>;
}

class FrankfurterService {
  private cache: Map<string, { data: ExchangeRates; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

  /**
   * Get latest exchange rates for a base currency
   * @param baseCurrency - Base currency code (default: USD)
   * @param targetCurrencies - Optional array of target currencies to fetch
   */
  async getLatestRates(
    baseCurrency: string = 'USD',
    targetCurrencies?: string[]
  ): Promise<ExchangeRates> {
    const cacheKey = `${baseCurrency}_${targetCurrencies?.join(',') || 'all'}`;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('Using cached exchange rates');
      return cached.data;
    }

    try {
      let url = `${FRANKFURTER_API_BASE}/latest?base=${baseCurrency}`;

      if (targetCurrencies && targetCurrencies.length > 0) {
        url += `&to=${targetCurrencies.join(',').toUpperCase()}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Frankfurter API error: ${response.status} ${response.statusText}`);
      }

      const data: ExchangeRates = await response.json();

      // Add base currency to rates (Frankfurter doesn't include it)
      data.rates[baseCurrency] = 1.0;

      // Cache the result
      this.cache.set(cacheKey, { data, timestamp: Date.now() });

      return data;
    } catch (error) {
      console.error('Error fetching exchange rates from Frankfurter:', error);
      throw error;
    }
  }

  /**
   * Get historical exchange rates for a specific date
   * @param date - Date in YYYY-MM-DD format
   * @param baseCurrency - Base currency code
   * @param targetCurrencies - Optional array of target currencies
   */
  async getHistoricalRates(
    date: string,
    baseCurrency: string = 'USD',
    targetCurrencies?: string[]
  ): Promise<ExchangeRates> {
    try {
      let url = `${FRANKFURTER_API_BASE}/${date}?base=${baseCurrency}`;

      if (targetCurrencies && targetCurrencies.length > 0) {
        url += `&to=${targetCurrencies.join(',').toUpperCase()}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Frankfurter API error: ${response.status} ${response.statusText}`);
      }

      const data: ExchangeRates = await response.json();

      // Add base currency to rates
      data.rates[baseCurrency] = 1.0;

      return data;
    } catch (error) {
      console.error('Error fetching historical rates from Frankfurter:', error);
      throw error;
    }
  }

  /**
   * Convert amount from one currency to another
   * @param amount - Amount to convert
   * @param fromCurrency - Source currency
   * @param toCurrency - Target currency
   * @param date - Optional date for historical rates (YYYY-MM-DD)
   */
  async convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    date?: string
  ): Promise<number> {
    if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
      return amount;
    }

    try {
      const rates = date
        ? await this.getHistoricalRates(date, fromCurrency, [toCurrency])
        : await this.getLatestRates(fromCurrency, [toCurrency]);

      const rate = rates.rates[toCurrency.toUpperCase()];

      if (!rate) {
        throw new Error(`Exchange rate not found for ${toCurrency}`);
      }

      return amount * rate;
    } catch (error) {
      console.error('Error converting currency:', error);
      throw error;
    }
  }

  /**
   * Get all available currencies
   */
  async getAvailableCurrencies(): Promise<Record<string, string>> {
    try {
      const response = await fetch(`${FRANKFURTER_API_BASE}/currencies`);

      if (!response.ok) {
        throw new Error(`Frankfurter API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching available currencies:', error);
      throw error;
    }
  }

  /**
   * Clear the cache (useful when user changes base currency preference)
   */
  clearCache(): void {
    this.cache.clear();
  }
}

export const frankfurterService = new FrankfurterService();
